import type { Address } from '$types/address';
import {
  bytesToBigInt,
  encodeFunctionData,
  parseAbi,
  parseEther,
  toBytes,
  zeroAddress,
  type Hex,
} from 'viem';
import type { MetaTransaction } from '$types/transaction';
import { config } from '$lib/constants/circlesV2Config';
import { publicClient } from '$utils/publicClient';
import { getMetriAddress } from '$utils/getMetriAddress';
import { MetriFeeSafeAddress } from '$lib/constants/metriFeeSafe';
import { stateUserPrimaryGroup } from '$states/userPrimaryGroup.svelte';
import { log } from '$utils/__log';
import { getOwnCrcTokens } from '$services/circlesV2Utils/envio/avatars/fetchAvatars';
import { bigIntToFloat } from '$utils/bigIntToFloat';
import { AbiHubV2 } from '$lib/constants/abis';
import { getWrappedERC20Address } from '$services/circlesV2Utils/swapCrc';
import { DemurrageOrInflationary } from '$types/DemurrageOrInflationary';
import { addAddressToComethSponsorList } from '$services/circlesV2Utils/comethSponsor';

export const calculateIssuance = async (address: Address) => {
  const result = await publicClient.readContract({
    address: config.v2HubAddress! as `0x${string}`,
    abi: parseAbi([
      `function calculateIssuance(address _human) external view returns (uint256, uint256, uint256)`,
    ]),
    functionName: 'calculateIssuance',
    args: [address],
  });

  return bigIntToFloat(result[0]);
};

export const getV1ContractAddress = async (address: Address) => {
  const result = await publicClient.readContract({
    address: config.v1HubAddress! as `0x${string}`,
    abi: parseAbi([`function userToToken(address _user) external view returns (address)`]),
    functionName: 'userToToken',
    args: [address],
  });
  return result as `0x${string}`;
};

export const isV1TokenStopped = async (tokenAddress: Address) => {
  if (tokenAddress === zeroAddress) {
    return true;
  }
  const result = await publicClient.readContract({
    abi: parseAbi([`function stopped() external view returns (bool)`]),
    functionName: 'stopped',
    address: tokenAddress as `0x${string}`,
  });
  return result as boolean;
};

export const mintV1Token = (tokenAddress: Address) => {
  return {
    data: encodeFunctionData({
      abi: parseAbi([`function update()`]),
      functionName: 'update',
    }),
    value: 0n,
    to: tokenAddress as `0x${string}`,
  };
};

export const stopV1Token = (tokenAddress: Address) => {
  return {
    data: encodeFunctionData({
      abi: parseAbi([`function stop()`]),
      functionName: 'stop',
    }),
    value: 0n,
    to: tokenAddress as `0x${string}`,
  };
};

export const calculateIssuanceWithCheck = (address: Address): MetaTransaction => {
  return {
    data: encodeFunctionData({
      abi: parseAbi([`function calculateIssuanceWithCheck(address _human)`]),
      functionName: 'calculateIssuanceWithCheck',
      args: [address],
    }),
    value: 0n,
    to: config.v2HubAddress! as Hex,
  };
};

export const personalMint = async (
  metriFee: number,
  inviterAddress: Address | undefined = undefined,
  inviterFee: number | undefined = undefined,
  primaryGroupFee: number | undefined = undefined,
): Promise<MetaTransaction[]> => {
  const MAX_AMOUNT_TO_WRAP = 500;
  const userAddress = getMetriAddress();
  const userTokenId = bytesToBigInt(toBytes(userAddress));
  const primaryGroupAddress = stateUserPrimaryGroup.current?.primaryGroup;
  const primaryGroupFeeCollector = stateUserPrimaryGroup.current?.feeCollectionAddress;

  // Get wrapped ERC20 address for user's demurrage tokens
  const wrappedTokenAddress = inviterAddress
    ? await getWrappedERC20Address(userAddress, DemurrageOrInflationary.DEMURRAGE)
    : zeroAddress;

  if (wrappedTokenAddress !== zeroAddress) {
    await addAddressToComethSponsorList(wrappedTokenAddress as Address);
  }

  const [amountToBeMinted, { balance: userOwnCrcWrappedBalance }] = await Promise.all([
    calculateIssuance(userAddress),
    getOwnCrcTokens(userAddress, true),
  ]);
  const amountToBeMintedAfterFees =
    amountToBeMinted - metriFee - (inviterFee ?? 0) - (primaryGroupFee ?? 0);
  const remainingToWrap = MAX_AMOUNT_TO_WRAP - userOwnCrcWrappedBalance;

  const transactions: MetaTransaction[] = [
    {
      data: encodeFunctionData({
        abi: parseAbi([`function personalMint()`]),
        functionName: 'personalMint',
      }),
      value: 0n,
      to: config.v2HubAddress! as Hex,
    },
    {
      data: encodeFunctionData({
        abi: parseAbi([
          `function safeTransferFrom(address _from, address _to, uint256 _id, uint256 _value, bytes memory _data)`,
        ]),
        functionName: 'safeTransferFrom',
        args: [
          userAddress,
          MetriFeeSafeAddress,
          userTokenId,
          parseEther(metriFee.toString()),
          '0x',
        ],
      }),
      value: 0n,
      to: config.v2HubAddress! as Hex,
    },
  ];

  if (inviterAddress && inviterFee) {
    // 1. Wrap user's demurrage tokens to ERC20
    transactions.push({
      to: config.v2HubAddress as Address,
      data: encodeFunctionData({
        abi: AbiHubV2,
        functionName: 'wrap',
        args: [userAddress, parseEther(inviterFee.toString()), 0], // 0 = demurrage type
      }) as Hex,
      value: 0n,
    });

    // 2. Transfer ERC20 tokens to inviter
    // these tokens will effectively be burned if inviterAddress is from bot farm
    transactions.push({
      to: wrappedTokenAddress,
      data: encodeFunctionData({
        abi: parseAbi(['function transfer(address to, uint256 amount) returns (bool)']),
        functionName: 'transfer',
        args: [inviterAddress, parseEther(inviterFee.toString())],
      }) as Hex,
      value: 0n,
    });
  }

  const validPrimaryGroupExists =
    primaryGroupFeeCollector !== zeroAddress &&
    primaryGroupAddress !== zeroAddress &&
    primaryGroupFee &&
    primaryGroupFeeCollector;

  if (validPrimaryGroupExists) {
    transactions.push({
      data: encodeFunctionData({
        abi: parseAbi([
          `function safeTransferFrom(address _from, address _to, uint256 _id, uint256 _value, bytes memory _data)`,
        ]),
        functionName: 'safeTransferFrom',
        args: [
          userAddress,
          primaryGroupFeeCollector,
          userTokenId,
          parseEther(primaryGroupFee.toString()),
          '0x',
        ],
      }),
      value: 0n,
      to: config.v2HubAddress! as Hex,
    });
  }

  if (remainingToWrap > 0) {
    transactions.push({
      data: encodeFunctionData({
        abi: parseAbi([`function wrap(address _avatar,uint256 _amount, uint8 _type)`]),
        functionName: 'wrap',
        args: [
          userAddress,
          parseEther(Math.min(remainingToWrap, amountToBeMintedAfterFees).toString()),
          // demurrage
          0,
        ],
      }),
      value: 0n,
      to: config.v2HubAddress! as Hex,
    });
  }

  log('All transactions:', transactions);

  return transactions;
};
